import React, { useState, useEffect } from 'react';
import * as duckdb from '@duckdb/duckdb-wasm';
import { DuckDBPlatform, DuckDBProvider, useDuckDB, useDuckDBResolver } from '@duckdb/react-duckdb';
import FileUpload from './components/FileUpload';
import ColumnSelector from './components/ColumnSelector';
import QueryInput from './components/QueryInput';
import TextToSQL from './components/TextToSQL';
import ResultPreview from './components/ResultPreview';
import ExportButtons from './components/ExportButtons';
import { parseCSVHeaders, validateCSVFile } from './utils/parseCsv';
import { createTableFromCSVFile, executeQuery, exportToCSV, QueryResult } from './utils/duckdb';
import { downloadCSV, downloadParquet } from './utils/export';
import { shouldShowAiTextToSql } from './utils/shouldShowAiTextToSql';

const TABLE_NAME = 'data_table';

// Create console logger
const logger = new duckdb.ConsoleLogger();

// Create bundles for the platform
const bundles: duckdb.DuckDBBundles = {
  mvp: {
    mainModule: new URL('@duckdb/duckdb-wasm/dist/duckdb-mvp.wasm', import.meta.url).href,
    mainWorker: new URL('@duckdb/duckdb-wasm/dist/duckdb-browser-mvp.worker.js', import.meta.url).href,
  },
  eh: {
    mainModule: new URL('@duckdb/duckdb-wasm/dist/duckdb-eh.wasm', import.meta.url).href,
    mainWorker: new URL('@duckdb/duckdb-wasm/dist/duckdb-browser-eh.worker.js', import.meta.url).href,
  }
};

function AppContent() {
  const [file, setFile] = useState<File | null>(null);
  const [columns, setColumns] = useState<string[]>([]);
  const [disableAutoDetect, setDisableAutoDetect] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queryResult, setQueryResult] = useState<QueryResult | null>(null);
  const [isQueryLoading, setIsQueryLoading] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [isPreview, setIsPreview] = useState(false);
  const [lastQuery, setLastQuery] = useState<string>('');
  const [aiGeneratedQuery, setAiGeneratedQuery] = useState<string>('');
  const [showAiTextToSql] = useState(() => shouldShowAiTextToSql());

  // Use DuckDB React hook - now properly typed
  const duckDBResolvable = useDuckDB();
  const resolveDuckDB = useDuckDBResolver();
  
  console.log('DuckDB Status:', duckDBResolvable.status);
  console.log('DuckDB Value:', duckDBResolvable.value);
  console.log('DuckDB Error:', duckDBResolvable.error);

  const db = duckDBResolvable.value;
  const duckDBLoading = duckDBResolvable.status === 1; // RUNNING
  const duckDBError = duckDBResolvable.error;

  // Trigger DuckDB initialization on component mount
  useEffect(() => {
    if (duckDBResolvable.status === 0) { // NONE - not started yet
      console.log('Triggering DuckDB initialization...');
      resolveDuckDB();
    }
  }, [duckDBResolvable.status, resolveDuckDB]);

  const handleFileSelect = async (selectedFile: File, disableAutoDetect: boolean) => {
    setDisableAutoDetect(disableAutoDetect);
    setIsLoading(true);
    setError(null);
    setColumns([]);
    setSelectedColumns([]);
    setQueryResult(null);
    
    try {
      // Validate file
      const validationError = validateCSVFile(selectedFile);
      if (validationError) {
        setError(validationError);
        return;
      }

      // Parse headers
      const headers = await parseCSVHeaders(selectedFile);
      setColumns(headers);
      setSelectedColumns(headers);
      setFile(selectedFile);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process file');
    } finally {
      setIsLoading(false);
    }
  };

  const handleColumnChange = (newColumns: string[]) => {
    setSelectedColumns(newColumns);
  };

  const handleQuerySubmit = async (query: string, preview: boolean = false) => {
    if (!db || !file || selectedColumns.length === 0) {
      setQueryError('Please upload a file and select columns first');
      return;
    }

    // Check DuckDB is fully initialized
    if (duckDBResolvable.status !== 3) { // 3 = COMPLETED
      setQueryError('DuckDB is still initializing. Please wait...');
      return;
    }

    setIsQueryLoading(true);
    setQueryError(null);
    setIsPreview(preview);
    setLastQuery(query);

    try {
      // Create table directly from CSV file using DuckDB's native CSV reader
      await createTableFromCSVFile(db, TABLE_NAME, file, selectedColumns, disableAutoDetect);

      // Execute query
      const result = await executeQuery(db, query);
      setQueryResult(result);
    } catch (err) {
      setQueryError(err instanceof Error ? err.message : 'Query failed');
    } finally {
      setIsQueryLoading(false);
    }
  };

  const handleExportCSV = async () => {
    if (!db || !lastQuery) return;
    
    try {
      const csvData = await exportToCSV(db, lastQuery);
      downloadCSV(csvData, 'export.csv');
    } catch (err) {
      setQueryError(err instanceof Error ? err.message : 'Export failed');
    }
  };

  const handleExportParquet = async () => {
    if (!db || !lastQuery) return;
    
    try {
      // For now, export as CSV since Parquet export is complex
      const csvData = await exportToCSV(db, lastQuery);
      downloadCSV(csvData, 'export.parquet');
    } catch (err) {
      setQueryError(err instanceof Error ? err.message : 'Export failed');
    }
  };

  if (duckDBLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center space-y-6">
          <div className="relative">
            <div className="w-20 h-20 mx-auto bg-blue-600 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-white animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
              </svg>
            </div>
            <div className="absolute -inset-2 bg-blue-600 rounded-full opacity-20 animate-ping"></div>
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-gray-900">Initializing DuckDB</h2>
            <p className="text-gray-600">Loading WebAssembly modules...</p>
          </div>
        </div>
      </div>
    );
  }

  if (duckDBError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center space-y-6 max-w-md">
          <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-gray-900">DuckDB Error</h2>
            <p className="text-red-600 text-sm">{duckDBError}</p>
            <button 
              onClick={() => window.location.reload()}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-6">
      <div className="max-w-6xl mx-auto space-y-12">
        {/* Header */}
        <header className="text-center py-12">
          <h1 className="neon-title mb-6">
            Data Explorer
          </h1>
          <p className="neon-subtitle mb-8">
            Memory-Efficient SQL Analysis Tool
          </p>
          <div className="mt-8 neon-panel mx-auto max-w-3xl p-6">
            <div className="neon-panel-header">
              System Status
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 font-mono text-sm">
              <div className="text-center">
                <div className="text-gray-400 mb-2">DuckDB Engine:</div>
                <div className={`font-mono text-lg ${
                  duckDBResolvable.status === 3 ? 'neon-glow-text' : 'text-yellow-400'
                }`}>
                  {duckDBResolvable.status === 3 ? '[READY]' : '[LOADING...]'}
                </div>
              </div>
              <div className="text-center">
                <div className="text-gray-400 mb-2">Memory:</div>
                <div className="neon-glow-text font-mono text-lg">[OK]</div>
              </div>
              <div className="text-center">
                <div className="text-gray-400 mb-2">WebAssembly:</div>
                <div className="text-blue-400 font-mono text-lg">[ACTIVE]</div>
              </div>
              <div className="text-center">
                <div className="text-gray-400 mb-2">AI (Text-to-SQL):</div>
                <div className="text-purple-400 font-mono text-lg">[ON-DEVICE]</div>
              </div>
            </div>
          </div>
        </header>

        {/* Step 1: File Upload */}
        <section className="flex justify-center">
          <div className="w-full max-w-4xl">
            <div className="neon-panel p-8">
              <div className="neon-panel-header">
                Step 1: Load Data File
              </div>
              <div className="flex justify-center">
                <FileUpload 
                  onFileSelect={handleFileSelect} 
                  disabled={isLoading || duckDBLoading}
                  initialAutoDetect={true}
                />
              </div>
              {error && (
                <div className="neon-error mt-6 max-w-2xl mx-auto text-center">
                  <strong>ERROR:</strong> {error}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Step 2: Column Selection */}
        {columns.length > 0 && (
          <section className="flex justify-center">
            <div className="w-full max-w-5xl">
              <div className="neon-panel p-8">
                <div className="neon-panel-header">
                  Step 2: Select Columns
                </div>
                <div className="flex justify-center">
                  <ColumnSelector
                    columns={columns}
                    selectedColumns={selectedColumns}
                    onColumnsChange={handleColumnChange}
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Step 3: Query Input */}
        {selectedColumns.length > 0 && (
          <section className="flex justify-center">
            <div className="w-full max-w-6xl">
              <div className="neon-panel p-8">
                <div className="neon-panel-header">
                  Step 3: SQL Query Terminal
                </div>
                <div className="flex flex-col items-center gap-10">
                  {showAiTextToSql && (
                    <>
                      <TextToSQL
                        tableName={TABLE_NAME}
                        columns={selectedColumns}
                        onSQLGenerated={setAiGeneratedQuery}
                        disabled={isQueryLoading || duckDBLoading}
                      />
                      <div className="w-full border-t border-purple-800 opacity-50" />
                    </>
                  )}
                  <QueryInput
                    onRunQuery={handleQuerySubmit}
                    disabled={isQueryLoading || duckDBLoading}
                    tableName={TABLE_NAME}
                    externalQuery={aiGeneratedQuery}
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Step 4: Results */}
        {(queryResult || isQueryLoading || queryError) && (
          <section className="flex justify-center">
            <div className="w-full max-w-7xl">
              <div className="neon-panel p-8">
                <div className="neon-panel-header">
                  Step 4: Query Results
                </div>
                <div className="flex justify-center">
                  <ResultPreview
                    result={queryResult}
                    isLoading={isQueryLoading}
                    error={queryError}
                    isPreview={isPreview}
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Step 5: Export */}
        {queryResult && !isPreview && (
          <section className="flex justify-center">
            <div className="w-full max-w-6xl">
              <div className="neon-panel p-8">
                <div className="neon-panel-header">
                  Step 5: Export Data
                </div>
                <div className="flex justify-center">
                  <ExportButtons
                    onExportCSV={handleExportCSV}
                    onExportParquet={handleExportParquet}
                    disabled={false}
                    hasResults={!!queryResult}
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="text-center py-12">
          <div className="neon-subtitle text-sm opacity-75">
            Powered by DuckDB-WASM • Built with React & Synthwave Vibes
          </div>
        </footer>
      </div>
    </div>
  );
}

function App() {
  return (
    <DuckDBPlatform logger={logger} bundles={bundles}>
      <DuckDBProvider>
        <AppContent />
      </DuckDBProvider>
    </DuckDBPlatform>
  );
}

export default App;
